/*
 * New Rock: "cargar más" en las colecciones (16/10/2026). Ver snippets/nrk-cargar-mas.liquid.
 *
 * - Al acercarse al final de la rejilla carga sola la página siguiente (?page=N con la
 *   Section Rendering API, la misma que usan los filtros del tema) y añade sus tarjetas.
 *   Lo hace CARGAS_AUTOMATICAS veces; después hay que pulsar el botón, para que el pie de
 *   página se pueda alcanzar.
 * - Los filtros y el orden del tema repintan #CollectionAjaxContent y lanzan
 *   "collection:reloaded": entonces se vuelve a empezar con el bloque nuevo.
 * - Botón atrás: al salir de la página se guarda en sessionStorage hasta qué página se había
 *   cargado y la posición; al volver se cargan esas páginas y se recoloca el scroll.
 * - No usa theme.reinitProductGridItem(): su initQuickShop() vuelve a poner listeners en
 *   todas las tarjetas y las ya cargadas abrirían la vista rápida dos veces. Las tarjetas
 *   nuevas se preparan una a una (AOS y precarga de la vista rápida al pasar el ratón).
 */
(() => {
  const CARGAS_AUTOMATICAS = 2;
  const MARGEN_AUTO = '0px 0px 800px 0px';
  const CADUCIDAD_MS = 30 * 60 * 1000;
  const PREFIJO_CLAVE = 'nrk-cargar-mas:';
  const SELECTOR_REJILLA = '.grid__item--content .grid--uniform';

  let actual = null;

  const clave = () => PREFIJO_CLAVE + location.pathname + location.search;
  const formato = new Intl.NumberFormat(document.documentElement.lang || undefined);

  function leerGuardado() {
    try {
      const g = JSON.parse(sessionStorage.getItem(clave()) || 'null');
      return g && Date.now() - g.t < CADUCIDAD_MS ? g : null;
    } catch (e) {
      return null;
    }
  }

  function guardar() {
    try {
      if (actual && actual.pagina > actual.inicio) {
        sessionStorage.setItem(clave(), JSON.stringify({ pagina: actual.pagina, y: window.scrollY, t: Date.now() }));
      } else {
        sessionStorage.removeItem(clave());
      }
    } catch (e) {
      /* sin sessionStorage no se recuerda la posición */
    }
  }

  function esVueltaAtras() {
    const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    return !!nav && nav.type === 'back_forward';
  }

  /* Lo que hace theme.reinitProductGridItem(), pero solo para las tarjetas nuevas */
  function prepararTarjetas(tarjetas) {
    if (window.AOS) window.AOS.refreshHard();
    const theme = window.theme;
    if (!theme || !theme.settings || !theme.settings.quickView || typeof theme.preloadProductModal !== 'function') return;
    tarjetas.forEach((el) => {
      if (!el.matches('.grid-product') || !el.dataset.productId) return;
      const precargar = () => {
        if (theme.config && theme.config.bpSmall) return;
        el.removeEventListener('mouseover', precargar);
        el.removeEventListener('focusin', precargar);
        theme.preloadProductModal(el.dataset.productHandle, el.dataset.productId, el.querySelector('.quick-product__btn'));
      };
      el.addEventListener('mouseover', precargar);
      el.addEventListener('focusin', precargar);
    });
  }

  class CargarMas {
    constructor(nodo) {
      this.nodo = nodo;
      this.seccion = nodo.closest('[data-section-id]');
      this.rejilla = this.seccion ? this.seccion.querySelector(SELECTOR_REJILLA) : null;
      this.inicio = this.pagina = parseInt(nodo.dataset.pagina, 10) || 1;
      this.paginas = parseInt(nodo.dataset.paginas, 10) || 1;
      this.porPagina = parseInt(nodo.dataset.porPagina, 10) || 0;
      this.total = parseInt(nodo.dataset.total, 10) || 0;
      this.automaticas = 0;
      this.cargando = false;
      this.observador = null;
      this.contador = nodo.querySelector('.nrk-cargar-mas__contador');
      this.barra = nodo.querySelector('.nrk-cargar-mas__barra span');
      this.error = nodo.querySelector('.nrk-cargar-mas__error');
      this.boton = nodo.querySelector('.nrk-cargar-mas__boton');
    }

    get valido() {
      return !!(this.rejilla && this.boton && this.porPagina > 0 && this.paginas > 1);
    }

    arrancar(restaurar) {
      document.documentElement.classList.add('nrk-cargar-mas-activo');
      this.nodo.hidden = false;
      this.boton.addEventListener('click', () => this.cargar(true));
      this.pintar();
      const g = restaurar ? leerGuardado() : null;
      if (g && g.pagina > this.pagina) {
        this.restaurar(g);
      } else {
        this.observar();
      }
    }

    parar() {
      if (this.observador) this.observador.disconnect();
      this.observador = null;
    }

    pintar() {
      const vistos = Math.min(this.pagina * this.porPagina, this.total);
      this.contador.textContent = this.nodo.dataset.textoContador
        .replace('{n}', formato.format(vistos))
        .replace('{total}', formato.format(this.total));
      this.barra.style.width = (this.total ? (100 * vistos) / this.total : 100) + '%';
      this.boton.hidden = this.pagina >= this.paginas;
    }

    observar() {
      this.parar();
      if (this.pagina >= this.paginas || this.automaticas >= CARGAS_AUTOMATICAS) return;
      if (!('IntersectionObserver' in window)) return;
      this.observador = new IntersectionObserver(
        (entradas) => {
          if (this.cargando || !entradas.some((e) => e.isIntersecting)) return;
          this.automaticas++;
          this.cargar(false);
        },
        { rootMargin: MARGEN_AUTO }
      );
      this.observador.observe(this.nodo);
    }

    url(pagina) {
      const u = new URL(location.href);
      u.searchParams.set('page', pagina);
      u.searchParams.set('section_id', this.seccion.dataset.sectionId);
      return u.toString();
    }

    async pedir(pagina) {
      const r = await fetch(this.url(pagina), { credentials: 'same-origin' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const rejilla = doc.querySelector(SELECTOR_REJILLA);
      if (!rejilla) throw new Error('La respuesta no trae la rejilla');
      return Array.from(rejilla.children);
    }

    /* Añade las tarjetas sin repetir producto: si el orden cambia entre una página y otra
       (el de "Disponibilidad" se recalcula cada noche) un producto podría salir dos veces. */
    anadir(tarjetas) {
      const ya = new Set(Array.from(this.rejilla.querySelectorAll('[data-product-id]'), (el) => el.dataset.productId));
      const nuevas = [];
      tarjetas.forEach((el) => {
        const id = el.dataset ? el.dataset.productId : '';
        if (id && ya.has(id)) return;
        if (id) ya.add(id);
        nuevas.push(this.rejilla.appendChild(document.adoptNode(el)));
      });
      return nuevas;
    }

    ocupado(si) {
      this.cargando = si;
      this.boton.disabled = si;
      this.boton.classList.toggle('btn--loading', si);
      this.boton.setAttribute('aria-busy', si ? 'true' : 'false');
      this.boton.textContent = si ? this.nodo.dataset.textoCargando : this.nodo.dataset.textoBoton;
    }

    async cargar(manual) {
      if (this.cargando || this.pagina >= this.paginas) return;
      this.ocupado(true);
      this.error.hidden = true;
      try {
        const nuevas = this.anadir(await this.pedir(this.pagina + 1));
        this.pagina++;
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        prepararTarjetas(nuevas);
        this.pintar();
        if (manual && nuevas[0]) {
          const enlace = nuevas[0].querySelector('a[href]');
          if (enlace) enlace.focus({ preventScroll: true });
        }
      } catch (e) {
        this.error.hidden = false;
        this.automaticas = CARGAS_AUTOMATICAS;
      } finally {
        this.ocupado(false);
        this.observar();
      }
    }

    async restaurar(g) {
      const hasta = Math.min(g.pagina, this.paginas);
      const paginas = [];
      for (let p = this.pagina + 1; p <= hasta; p++) paginas.push(p);
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      this.ocupado(true);
      try {
        const lotes = await Promise.all(paginas.map((p) => this.pedir(p)));
        const nuevas = [];
        lotes.forEach((tarjetas) => nuevas.push(...this.anadir(tarjetas)));
        this.pagina = hasta;
        this.automaticas = Math.min(CARGAS_AUTOMATICAS, hasta - this.inicio);
        prepararTarjetas(nuevas);
        this.pintar();
        window.scrollTo(0, g.y);
      } catch (e) {
        this.error.hidden = false;
      } finally {
        this.ocupado(false);
        this.observar();
      }
    }
  }

  function iniciar(restaurar) {
    if (actual) actual.parar();
    actual = null;
    const nodo = document.querySelector('[data-nrk-cargar-mas]');
    const c = nodo ? new CargarMas(nodo) : null;
    if (!c || !c.valido) {
      document.documentElement.classList.remove('nrk-cargar-mas-activo');
      return;
    }
    actual = c;
    c.arrancar(restaurar);
  }

  window.addEventListener('pagehide', guardar);
  document.addEventListener('collection:reloaded', () => iniciar(false));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => iniciar(esVueltaAtras()));
  } else {
    iniciar(esVueltaAtras());
  }
})();
